import { FC, useEffect, useMemo, useState } from 'react';
import { DateRange } from 'react-day-picker';
import { endOfMonth, endOfYear, format, startOfMonth, startOfYear, subDays } from 'date-fns';
import { es } from 'date-fns/locale';
import { CalendarIcon, X } from 'lucide-react';
import { Button, ButtonProps } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

type DateRangePickerProps = {
  value: DateRange | undefined;
  onChange: (range: DateRange | undefined) => void;
  placeholder?: string;
  className?: string;
  align?: 'start' | 'center' | 'end';
  triggerVariant?: ButtonProps['variant'];
  badgeText?: string;
};

const PRESET_BUILDERS = [
  {
    label: 'Este mes',
    getRange: () => ({ from: startOfMonth(new Date()), to: endOfMonth(new Date()) }),
  },
  {
    label: 'Mes anterior',
    getRange: () => {
      const previousMonth = subDays(startOfMonth(new Date()), 1);
      return {
        from: startOfMonth(previousMonth),
        to: endOfMonth(previousMonth),
      };
    },
  },
  {
    label: 'Últimos 30 días',
    getRange: () => ({ from: subDays(new Date(), 29), to: new Date() }),
  },
  {
    label: 'Últimos 90 días',
    getRange: () => ({ from: subDays(new Date(), 89), to: new Date() }),
  },
  {
    label: 'Año en curso',
    getRange: () => ({ from: startOfYear(new Date()), to: endOfYear(new Date()) }),
  },
];

const getRangeLabel = (range: DateRange | undefined, placeholder: string, separator = ' - ') => {
  if (!range?.from) return placeholder;
  if (!range.to) return `${format(range.from, 'dd/MM/yyyy')}${separator}...`;
  return `${format(range.from, 'dd/MM/yyyy')}${separator}${format(range.to, 'dd/MM/yyyy')}`;
};

const getRangeKey = (range: DateRange | undefined) => {
  if (!range?.from || !range?.to) return '';
  return `${format(range.from, 'yyyy-MM-dd')}:${format(range.to, 'yyyy-MM-dd')}`;
};

const DateRangePicker: FC<DateRangePickerProps> = ({
  value,
  onChange,
  placeholder = 'Seleccionar período',
  className,
  align = 'start',
  triggerVariant = 'outline',
  badgeText,
}) => {
  const [open, setOpen] = useState(false);
  const [draftRange, setDraftRange] = useState<DateRange | undefined>(value);
  const [month, setMonth] = useState<Date>(value?.from ?? new Date());

  useEffect(() => {
    if (!open) {
      setDraftRange(value);
      setMonth(value?.from ?? new Date());
    }
  }, [open, value]);

  const hasCompleteRange = Boolean(draftRange?.from && draftRange?.to);
  const hasAnyRange = Boolean(draftRange?.from || draftRange?.to);

  const rangeLabel = useMemo(() => getRangeLabel(value, placeholder), [placeholder, value]);
  const draftRangeLabel = useMemo(
    () => getRangeLabel(draftRange, 'Elegí un período', ' ~ '),
    [draftRange],
  );
  const activePreset = useMemo(() => {
    const currentKey = getRangeKey(draftRange);
    if (!currentKey) return null;

    return PRESET_BUILDERS.find((preset) => getRangeKey(preset.getRange()) === currentKey)?.label;
  }, [draftRange]);

  const applyRange = () => {
    if (!hasAnyRange) {
      onChange(undefined);
      setOpen(false);
      return;
    }

    if (!draftRange?.from || !draftRange.to) {
      return;
    }

    onChange(draftRange);
    setOpen(false);
  };

  const clearDraftRange = () => {
    setDraftRange(undefined);
    setMonth(new Date());
  };

  return (
    <div className="w-full">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <div className="flex flex-col items-start">
            <Button
              variant={triggerVariant}
              className={cn(
                'h-11 w-full justify-start rounded-xl px-4 text-left font-normal shadow-sm',
                !value?.from && 'text-muted-foreground',
                className,
              )}
            >
              <CalendarIcon className="mr-2 h-4 w-4 opacity-50" />
              <span className="truncate">{rangeLabel}</span>
            </Button>
            {badgeText && value?.from && value?.to ? (
              <span className="bg-teal-600 py-2 px-5 rounded-b-[12px] text-white text-[11px]">
                {badgeText}
              </span>
            ) : null}
          </div>
        </PopoverTrigger>
        <PopoverContent
          className="flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white p-0 shadow-xl"
          align={align}
          side="bottom"
          sideOffset={8}
          collisionPadding={16}
          sticky="always"
          style={{
            width: 'min(42rem, calc(100vw - 1rem), var(--radix-popover-content-available-width))',
            maxHeight: 'min(80vh, var(--radix-popover-content-available-height))',
          }}
        >
          <div className="min-h-0 space-y-3 overflow-y-auto p-3">
            <div className="rounded-2xl border border-slate-200 bg-white px-3 py-2.5 shadow-sm">
              <div className="flex items-center gap-2.5">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-500">
                  <CalendarIcon className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">
                    Período seleccionado
                  </p>
                  <p className="truncate text-[13px] font-medium text-slate-900">
                    {draftRangeLabel}
                  </p>
                </div>
                {hasAnyRange ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={clearDraftRange}
                    className="h-8 w-8 shrink-0 rounded-xl text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                    aria-label="Limpiar período"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                ) : null}
              </div>
            </div>

            <div className="grid gap-3 md:grid-cols-[8rem_minmax(0,1fr)]">
              <aside className="space-y-2.5 md:border-r md:border-slate-200 md:pr-3">
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">
                    Rangos rápidos
                  </p>
                </div>
                <div className="grid gap-2 sm:grid-cols-2 md:grid-cols-1">
                  {PRESET_BUILDERS.map((preset) => {
                    const isActive = preset.label === activePreset;

                    return (
                      <Button
                        key={preset.label}
                        type="button"
                        variant="ghost"
                        onClick={() => {
                          const nextRange = preset.getRange();
                          setDraftRange(nextRange);
                          if (nextRange.from) {
                            setMonth(nextRange.from);
                          }
                        }}
                        className={cn(
                          'h-auto min-h-0 justify-start rounded-xl border px-2.5 py-2 text-left text-xs font-medium shadow-sm transition-colors',
                          isActive
                            ? 'border-primary/30 bg-primary/10 text-primary hover:bg-primary/10'
                            : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50 hover:text-slate-900',
                        )}
                      >
                        {preset.label}
                      </Button>
                    );
                  })}
                </div>
              </aside>

              <div className="overflow-x-hidden rounded-2xl border border-slate-200 bg-slate-50/60 p-2">
                <Calendar
                  mode="range"
                  numberOfMonths={2}
                  showOutsideDays
                  fixedWeeks
                  selected={draftRange}
                  onSelect={(range) => {
                    setDraftRange(range);
                    if (range?.from) {
                      setMonth(range.from);
                    }
                  }}
                  month={month}
                  onMonthChange={setMonth}
                  locale={es}
                  className="p-0"
                  classNames={{
                    months: 'flex flex-col gap-2.5 sm:flex-row sm:gap-2.5',
                    month: 'min-w-0 space-y-2.5',
                    caption:
                      'relative flex h-8 items-center justify-center rounded-xl border border-slate-200 bg-white px-7 shadow-sm',
                    caption_label:
                      'text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-700',
                    nav_button:
                      'absolute h-5 w-5 rounded-lg border-0 bg-transparent p-0 text-slate-400 opacity-100 hover:bg-slate-100 hover:text-slate-700',
                    nav_button_previous: 'left-1.5',
                    nav_button_next: 'right-1.5',
                    table: 'w-full border-collapse',
                    head_row: 'flex w-full',
                    head_cell:
                      'w-7 rounded-md text-[0.6rem] font-medium uppercase tracking-[0.1em] text-slate-400',
                    row: 'mt-0.5 flex w-full',
                    cell: 'relative h-7 w-7 p-0 text-center text-[11px] [&:has([aria-selected])]:bg-primary/10 [&:has([aria-selected].day-range-start)]:rounded-l-lg [&:has([aria-selected].day-range-end)]:rounded-r-lg first:[&:has([aria-selected])]:rounded-l-lg last:[&:has([aria-selected])]:rounded-r-lg',
                    day: 'h-7 w-7 rounded-lg p-0 text-[11px] font-medium text-slate-700 hover:bg-slate-100',
                    day_selected:
                      'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground',
                    day_today: 'bg-slate-100 text-slate-900',
                    day_outside: 'text-slate-300 opacity-100',
                    day_disabled: 'text-slate-300 opacity-100',
                    day_range_start: 'day-range-start rounded-lg',
                    day_range_end: 'day-range-end rounded-lg',
                    day_range_middle:
                      'aria-selected:rounded-none aria-selected:bg-primary/10 aria-selected:text-slate-900',
                  }}
                />
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-2 border-t border-slate-200 bg-slate-50/80 px-3 py-2.5 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-[11px] text-slate-500">
              {!hasAnyRange
                ? 'No hay período seleccionado.'
                : hasCompleteRange
                  ? `Desde ${format(draftRange!.from!, 'dd/MM/yyyy')} hasta ${format(draftRange!.to!, 'dd/MM/yyyy')}`
                  : 'Seleccioná la segunda fecha para completar el rango.'}
            </p>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setOpen(false)}
                className="h-8 rounded-xl px-3 text-slate-600 hover:bg-white hover:text-slate-900"
              >
                Cancelar
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={applyRange}
                disabled={hasAnyRange && !hasCompleteRange}
                className="h-8 rounded-xl px-4"
              >
                Aplicar
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default DateRangePicker;
